import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  HelpOutline,
  Phone,
  Email,
  Chat,
  LocationOn,
  ExpandMore,
  Send,
  AttachFile,
  QuestionAnswer,
  Support,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';

const faqs = [
  {
    category: 'Booking',
    questions: [
      {
        q: 'How do I make a reservation?',
        a: 'You can make a reservation through our website by clicking on "New Booking" in the My Bookings section, or by calling our reservation desk at (555) 123-4567.',
      },
      {
        q: 'What is the cancellation policy?',
        a: 'Free cancellation is available up to 48 hours before check-in. Cancellations made within 48 hours will incur a one-night charge.',
      },
      {
        q: 'Can I modify my booking?',
        a: 'Yes, you can modify your booking up to 24 hours before check-in through your account or by contacting our support team.',
      },
    ],
  },
  {
    category: 'Check-in & Check-out',
    questions: [
      {
        q: 'What time is check-in and check-out?',
        a: 'Standard check-in time is 3:00 PM and check-out is 11:00 AM. Early check-in and late check-out may be available upon request.',
      },
      {
        q: 'What documents do I need for check-in?',
        a: 'You will need a valid government-issued photo ID and the credit card used for booking.',
      },
      {
        q: 'Can I check in early?',
        a: 'Early check-in is subject to availability and may incur an additional charge. Please contact us in advance to arrange.',
      },
    ],
  },
  {
    category: 'Services & Amenities',
    questions: [
      {
        q: 'Do you offer room service?',
        a: 'Yes, we offer 24/7 room service. You can order through the Services section in your account or by calling extension 5 from your room.',
      },
      {
        q: 'Is WiFi available?',
        a: 'Yes, complimentary high-speed WiFi is available throughout the hotel for all guests.',
      },
      {
        q: 'Do you have parking facilities?',
        a: 'Yes, we offer both valet and self-parking. Parking fees apply and vary by duration.',
      },
    ],
  },
  {
    category: 'Payment & Billing',
    questions: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, MasterCard, American Express), debit cards, and cash.',
      },
      {
        q: 'When will I be charged?',
        a: 'Your card will be charged at the time of booking or upon check-in, depending on the rate type selected.',
      },
      {
        q: 'Can I get an invoice for my stay?',
        a: 'Yes, all invoices are available in the Invoices section of your account and can be downloaded as PDF.',
      },
    ],
  },
];

const supportTickets = [
  {
    id: 'TKT-001',
    subject: 'Request for late checkout',
    date: '2025-11-15',
    status: 'Resolved',
    response: 'Your late checkout has been approved until 2:00 PM.',
  },
  {
    id: 'TKT-002',
    subject: 'Question about spa services',
    date: '2025-11-14',
    status: 'Resolved',
    response: 'Our spa is open daily from 9 AM to 9 PM. We recommend booking in advance.',
  },
];

export default function CustomerSupport() {
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [tickets, setTickets] = useState(supportTickets);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'General Inquiry',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedFaq, setExpandedFaq] = useState('');

  const handleSubmitTicket = () => {
    if (!ticketForm.subject || !ticketForm.message) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    const newTicket = {
      id: `TKT-${String(tickets.length + 1).padStart(3, '0')}`,
      subject: ticketForm.subject,
      date: new Date().toISOString().split('T')[0],
      status: 'Open',
      response: null,
    };

    setTickets([newTicket, ...tickets]);
    setSnackbar({
      open: true,
      message: 'Support ticket submitted successfully! We will respond within 24 hours.',
      severity: 'success',
    });
    setTicketDialogOpen(false);
    setTicketForm({ subject: '', category: 'General Inquiry', message: '' });
  };

  const contactMethods = [
    {
      icon: Phone,
      title: '24/7 Phone Support',
      detail: '(555) 123-4567',
      color: '#1976d2',
      action: 'Call Now',
    },
    {
      icon: Email,
      title: 'Email Support',
      detail: 'support@hotelops.com',
      color: '#2e7d32',
      action: 'Send Email',
    },
    {
      icon: Chat,
      title: 'Live Chat',
      detail: 'Average response time: 2 min',
      color: '#ed6c02',
      action: 'Start Chat',
    },
    {
      icon: LocationOn,
      title: 'Visit Us',
      detail: '123 Hotel Street, NY 10001',
      color: '#9c27b0',
      action: 'Get Directions',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Help & Support
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We're here to help! Find answers or get in touch with our support team
        </Typography>
      </Box>

      <Grid container spacing={3} mb={3}>
        {contactMethods.map((method) => {
          const IconComponent = method.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={method.title}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: method.color,
                      width: 56,
                      height: 56,
                      margin: '0 auto',
                      mb: 2,
                    }}
                  >
                    <IconComponent />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {method.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {method.detail}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    sx={{ bgcolor: method.color, '&:hover': { bgcolor: method.color } }}
                    onClick={() => method.title === 'Live Chat' && setChatDialogOpen(true)}
                  >
                    {method.action}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Frequently Asked Questions
              </Typography>
            </Box>

            {faqs.map((category) => (
              <Box key={category.category} mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  {category.category}
                </Typography>
                {category.questions.map((faq, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === `${category.category}-${index}`}
                    onChange={() =>
                      setExpandedFaq(
                        expandedFaq === `${category.category}-${index}`
                          ? ''
                          : `${category.category}-${index}`
                      )
                    }
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2" fontWeight="medium">
                        {faq.q}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Still have questions?
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Can't find the answer you're looking for? Submit a support ticket and our team will get back to you.
            </Typography>
            <Button
              variant="contained"
              startIcon={<QuestionAnswer />}
              onClick={() => setTicketDialogOpen(true)}
            >
              Submit Support Ticket
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Support Hours
            </Typography>
            <List>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#1976d2' }}>
                    <Schedule />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Phone Support"
                  secondary="24/7 - Always available"
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#2e7d32' }}>
                    <Chat />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Live Chat"
                  secondary="6 AM - 12 AM daily"
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#ed6c02' }}>
                    <Email />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Email Support"
                  secondary="Response within 24 hours"
                />
              </ListItem>
            </List>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              My Support Tickets
            </Typography>
            {tickets.length > 0 ? (
              <List>
                {tickets.map((ticket, index) => (
                  <React.Fragment key={ticket.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: ticket.status === 'Resolved' ? '#2e7d32' : '#ed6c02' }}>
                          {ticket.status === 'Resolved' ? <CheckCircle /> : <Support />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {ticket.id}
                            </Typography>
                            <Chip
                              label={ticket.status}
                              size="small"
                              color={ticket.status === 'Resolved' ? 'success' : 'warning'}
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {ticket.subject}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(ticket.date).toLocaleDateString()}
                            </Typography>
                            {ticket.response && (
                              <Typography variant="caption" display="block" mt={1} sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                                {ticket.response}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No support tickets yet
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <QuestionAnswer color="primary" />
            <Typography variant="h6">Submit Support Ticket</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              select
              label="Category"
              value={ticketForm.category}
              onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
              margin="normal"
              SelectProps={{ native: true }}
            >
              <option value="General Inquiry">General Inquiry</option>
              <option value="Booking Issue">Booking Issue</option>
              <option value="Payment Issue">Payment Issue</option>
              <option value="Service Request">Service Request</option>
              <option value="Complaint">Complaint</option>
              <option value="Other">Other</option>
            </TextField>

            <TextField
              fullWidth
              label="Subject"
              value={ticketForm.subject}
              onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
              margin="normal"
              required
              placeholder="Brief description of your issue"
            />

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Message"
              value={ticketForm.message}
              onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
              margin="normal"
              required
              placeholder="Please provide detailed information about your request..."
            />

            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              sx={{ mt: 1 }}
            >
              Attach File
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitTicket} startIcon={<Send />}>
            Submit Ticket
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={chatDialogOpen} onClose={() => setChatDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Chat color="primary" />
            <Typography variant="h6">Live Chat Support</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
            <Box flex={1} sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Chat feature coming soon! For immediate assistance, please call (555) 123-4567
              </Typography>
            </Box>
            <TextField
              fullWidth
              placeholder="Type your message..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton color="primary">
                      <Send />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
